import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "./api";
import { Time } from "./time";

// Shared moderation pieces: view filter (active / archived), selection, bulk Archive / Restore / Delete
// permanently (typed "DELETE"), and "Block submitter". Record text is visitor input: rendered as text only.

export function useSelection() {
  const [sel, setSel] = useState(() => new Set());
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const clear = () => setSel(new Set());
  return { sel, toggle, clear, setSel };
}

export function RowCheck({ id, selection, label }) {
  return <input type="checkbox" aria-label={`select ${label || id}`} data-testid={`select-${id}`}
                checked={selection.sel.has(id)} onChange={() => selection.toggle(id)} />;
}

export function ModerationBar({ kind, view, setView, selection, counts, styles, onDone }) {
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");
  const ids = [...selection.sel];
  const run = async (action) => {
    let body = { ids, reason };
    if (action === "delete") {
      const typed = window.prompt(`Permanently delete ${ids.length} record(s) and the personal data in them? Type DELETE to confirm.`);
      if (typed !== "DELETE") { setMsg("Not deleted."); return; }
      body = { ...body, confirm: typed };
    }
    try {
      const r = await api.moderationAction(kind, action, body);
      setMsg(action === "delete" ? `Deleted ${r.data.deleted}${r.data.items_deleted ? ` (+${r.data.items_deleted} reported-only item)` : ""}.`
        : action === "archive" ? `Archived ${r.data.archived}.` : `Restored ${r.data.restored}.`);
      selection.clear(); onDone && onDone();
    } catch (ex) { setMsg("Error: " + ex.message); }
  };
  return (
    <div style={{ ...styles.row }} data-testid={`moderation-bar-${kind}`}>
      <label className="hs-label" htmlFor={`view-${kind}`} style={{ marginBottom: 0 }}>Show</label>
      <select id={`view-${kind}`} data-testid={`view-${kind}`} style={styles.select} value={view} onChange={(e) => { selection.clear(); setView(e.target.value); }}>
        <option value="active">active{counts ? ` (${counts.active})` : ""}</option>
        <option value="archived">archived{counts ? ` (${counts.archived})` : ""}</option>
      </select>
      {ids.length > 0 && (
        <>
          <strong>{ids.length} selected</strong>
          <label className="hs-label" htmlFor={`reason-${kind}`} style={{ marginBottom: 0 }}>Reason</label>
          <input id={`reason-${kind}`} style={{ ...styles.input, minWidth: 180 }} value={reason} onChange={(e) => setReason(e.target.value)} />
          {view === "archived"
            ? <button type="button" data-testid={`restore-${kind}`} style={styles.btnDim} onClick={() => run("restore")}>Restore</button>
            : <button type="button" data-testid={`archive-${kind}`} style={styles.btnDim} onClick={() => run("archive")}>Archive</button>}
          <button type="button" data-testid={`delete-${kind}`} style={styles.btnDim} onClick={() => run("delete")}>Delete permanently</button>
        </>)}
      {msg && <span style={{ fontSize: 12, color: "var(--hs-text-muted)" }} data-testid={`moderation-msg-${kind}`}>{msg}</span>}
    </div>
  );
}

export function BlockButton({ kind, id, styles, onDone }) {
  const block = async () => {
    const days = window.prompt("Block this submitter (hashed IP and email). Days until it expires (blank = never):", "90");
    if (days === null) return;
    try {
      await api.blockSubmitter(kind, id, { expires_days: days.trim() ? Number(days) : null, reason: "" });
      window.alert("Blocked. They will see the normal success message, but nothing they send is stored.");
      onDone && onDone();
    } catch (ex) { window.alert("Error: " + ex.message); }
  };
  return <button type="button" data-testid={`block-${id}`} style={styles.btnDim} onClick={block}>Block submitter</button>;
}

export function UserReports({ styles }) {
  const [view, setView] = useState("active");
  const [data, setData] = useState({ entries: [], counts: null });
  const selection = useSelection();
  const load = useCallback(() => api.moderationList("reports", view).then((r) => setData(r.data)), [view]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="user-reports-page">
      <h2>User reports</h2>
      <ModerationBar kind="reports" view={view} setView={setView} selection={selection} counts={data.counts} styles={styles} onDone={load} />
      <div style={styles.card}>
        <table style={styles.tbl}>
          <thead><tr><th style={styles.th}></th><th style={styles.th}>When</th><th style={styles.th}>Server</th><th style={styles.th}>What happened</th>
            <th style={styles.th}>Email</th><th style={styles.th}>Status</th><th style={styles.th}></th></tr></thead>
          <tbody>{data.entries.map((r) => (
            <tr key={r.id} data-testid={`report-row-${r.id}`}>
              <td style={styles.td}><RowCheck id={r.id} selection={selection} label={r.server_input} /></td>
              <td style={styles.td}><Time value={r.created_at} /></td>
              <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)" }}>
                {r.item_id ? <Link className="hs-link" to={`/admin/items/${r.item_id}`}>{r.server_input}</Link> : r.server_input}
                {r.item && r.item.reported_only && !r.item.source_id && <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>only known from reports</div>}
              </td>
              <td style={{ ...styles.td, whiteSpace: "pre-wrap", maxWidth: 380 }}>{r.what_happened}</td>
              <td style={styles.td}>{r.email || "—"}</td>
              <td style={styles.td}>{r.status}</td>
              <td style={styles.td}><BlockButton kind="reports" id={r.id} styles={styles} onDone={load} /></td>
            </tr>))}
          {data.entries.length === 0 && <tr><td style={styles.td} colSpan={7}>No reports.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

export function Blocklist({ styles }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.blocklist().then((r) => setRows(r.data.blocks)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { await api.removeBlock(id); load(); };
  return (
    <div data-testid="blocklist-page">
      <h2>Blocked submitters</h2>
      <p style={{ color: "var(--hs-text-muted)" }}>Only keyed hashes are stored, never the IP address or email. Blocked submitters see the normal success message and nothing they send is kept.</p>
      <div style={styles.card}>
        <table style={styles.tbl}>
          <thead><tr><th style={styles.th}>Type</th><th style={styles.th}>Hash</th><th style={styles.th}>From</th><th style={styles.th}>Expires</th>
            <th style={styles.th}>Reason</th><th style={styles.th}></th></tr></thead>
          <tbody>{rows.map((b) => (
            <tr key={b.id} data-testid={`block-row-${b.id}`}>
              <td style={styles.td}>{b.kind}</td><td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)" }}>{(b.value_hash || "").slice(0, 12)}…</td>
              <td style={styles.td}>{b.source}</td>
              <td style={styles.td}>{b.expires_at ? <Time value={b.expires_at} dateOnly /> : "never"}{b.expired && " (expired)"}</td>
              <td style={styles.td}>{b.reason || "—"}</td>
              <td style={styles.td}><button type="button" data-testid={`unblock-${b.id}`} style={styles.btnDim} onClick={() => remove(b.id)}>Remove</button></td>
            </tr>))}
          {rows.length === 0 && <tr><td style={styles.td} colSpan={6}>No blocks.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

export function PrivacyRequests({ styles }) {
  const [email, setEmail] = useState("");
  const [found, setFound] = useState(null);
  const [msg, setMsg] = useState("");
  const find = async (e) => {
    e.preventDefault(); setMsg("");
    try { const r = await api.privacyFind(email.trim()); setFound(r.data); } catch (ex) { setMsg("Error: " + ex.message); }
  };
  const del = async () => {
    const typed = window.prompt(`Delete ALL data for ${email.trim()} (waitlist, reports, appeals, email log)? Type DELETE to confirm.`);
    if (typed !== "DELETE") { setMsg("Not deleted."); return; }
    try { const r = await api.privacyDelete(email.trim(), typed); setMsg(`Deleted: ${Object.entries(r.data.deleted).map(([k, v]) => `${k} ${v}`).join(", ")}.`); setFound(null); }
    catch (ex) { setMsg("Error: " + ex.message); }
  };
  const total = found ? Object.values(found.counts).reduce((a, b) => a + b, 0) : 0;
  return (
    <div data-testid="privacy-requests-page">
      <h2>Privacy requests</h2>
      <p style={{ color: "var(--hs-text-muted)" }}>Find and delete everything stored for one email address. The audit log records a keyed hash of the address, never the address.</p>
      <form onSubmit={find} style={styles.row}>
        <label className="hs-label" htmlFor="pr-email" style={{ marginBottom: 0 }}>Email</label>
        <input id="pr-email" data-testid="privacy-email" type="email" required style={{ ...styles.input, minWidth: 280 }} value={email} onChange={(e) => setEmail(e.target.value)} />
        <button type="submit" data-testid="privacy-find" style={styles.btnDim}>Find</button>
      </form>
      {found && (
        <div style={styles.card} data-testid="privacy-found">
          {Object.entries(found.found).map(([k, rows]) => (
            <div key={k} style={{ marginBottom: 8 }}><strong>{k}</strong>: {rows.length}
              {rows.slice(0, 20).map((r, i) => <div key={i} style={{ fontSize: 12, color: "var(--hs-text-muted)", whiteSpace: "pre-wrap" }}>
                <Time value={r.created_at} dateOnly /> {r.server_input || r.item_identifier || r.company || r.subject || ""} {r.what_happened || r.message || r.mcp_servers || ""}</div>)}
            </div>))}
          {total > 0
            ? <button type="button" data-testid="privacy-delete" style={styles.btn} onClick={del}>Delete all {total} record(s)</button>
            : <div>No data stored for this address.</div>}
        </div>)}
      {msg && <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }} data-testid="privacy-msg">{msg}</div>}
    </div>
  );
}
